import { useEffect } from 'react';
import { featured, projects, articles } from '../requests';

const SITE_URL = 'https://jerwingubatportfolio.vercel.app';
const SITE_NAME = 'Jerwin Gubat Portfolio';
const AUTHOR_NAME = 'Jerwin Gubat';

const absoluteUrl = (path) => {
    if (!path) return undefined;
    if (/^https?:\/\//i.test(path)) return path;
    return `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`;
};

const normalizeCategories = (category) =>
    typeof category === 'string'
        ? category.split(',').map((c) => c.trim()).filter(Boolean)
        : [];

const uniqueByName = (items) => {
    const seen = new Set();
    return items.filter((item) => {
        const key = item.name.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
};

const buildStructuredData = () => {
    const portfolioItems = [...featured, ...projects];
    const uniqueProjects = uniqueByName(
        portfolioItems.map((item) => ({
            name: item.title,
            description: item.description,
            image: absoluteUrl(item.imageUrl),
            liveUrl: item.liveUrl,
            sourceUrl: item.githubUrl,
            tags: item.tags || [],
            category: normalizeCategories(item.category)
        }))
    );

    const projectList = {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: 'Projects by Jerwin Gubat',
        url: `${SITE_URL}/#projects`,
        itemListElement: uniqueProjects.map((project, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            item: {
                '@type': 'SoftwareApplication',
                name: project.name,
                description: project.description,
                applicationCategory: project.category[0] || 'SoftwareApplication',
                operatingSystem: 'Cross-platform',
                image: project.image,
                url: project.liveUrl || project.sourceUrl,
                keywords: project.tags.join(', '),
                author: {
                    '@type': 'Person',
                    name: AUTHOR_NAME,
                    url: SITE_URL
                }
            }
        }))
    };

    const articleList = {
        '@context': 'https://schema.org',
        '@type': 'Blog',
        name: 'Articles & Insights by Jerwin Gubat',
        url: `${SITE_URL}/#articles`,
        author: {
            '@type': 'Person',
            name: AUTHOR_NAME,
            url: SITE_URL
        },
        blogPost: articles.map((article) => ({
            '@type': 'BlogPosting',
            headline: article.title,
            description: article.description,
            image: absoluteUrl(article.imageUrl),
            url: article.link,
            datePublished: article.date,
            keywords: (article.categories || []).join(', '),
            author: {
                '@type': 'Person',
                name: AUTHOR_NAME,
                url: SITE_URL
            }
        }))
    };

    return [projectList, articleList];
};

export default function Seo() {
    useEffect(() => {
        const previousTitle = document.title;
        document.title = 'Jerwin Gubat | Freelance Fullstack Developer in Cavite, Philippines';

        const structuredData = buildStructuredData();
        const scriptNodes = structuredData.map((data) => {
            const script = document.createElement('script');
            script.type = 'application/ld+json';
            script.dataset.seo = 'true';
            script.textContent = JSON.stringify(data);
            document.head.appendChild(script);
            return script;
        });

        return () => {
            document.title = previousTitle;
            scriptNodes.forEach((node) => node.remove());
        };
    }, []);

    return null;
}

export { SITE_URL, SITE_NAME, absoluteUrl };
